import { z } from 'zod';
import { ObjectiveSchema } from './analysis.ts';
import { ChipSchema } from './squad.ts';

/**
 * The record of a recommendation, scored honestly once its gameweek is
 * played — the feature notes call this "the honesty ledger" for a reason: an
 * entry that always reads well is worthless, so nothing here is allowed to
 * summarise itself into a single flattering number.
 */

/** One player identified for later scoring: the name as it appeared on the
 * analysis, plus the FPL element id when known. A hand-typed squad player
 * with no `fplId` scores as 0 points once settled — the same convention
 * `/:id/live-points` already uses for an unmatched player. */
export const LedgerPlayerRef = z.object({
  name: z.string(),
  fplId: z.number().int().positive().optional(),
});
export type LedgerPlayerRef = z.infer<typeof LedgerPlayerRef>;

/**
 * The captaincy call as recorded: what was recommended, the second-best
 * option, and — distinct from both — whoever the manager actually had
 * armbanded at analysis time. `own` is what makes the captain outcome a
 * comparison rather than a fact about the recommendation alone.
 */
export const LedgerCaptainCall = z.object({
  pick: LedgerPlayerRef,
  alternative: LedgerPlayerRef.optional(),
  own: LedgerPlayerRef,
});
export type LedgerCaptainCall = z.infer<typeof LedgerCaptainCall>;

export const LedgerTransferCall = z.object({
  out: LedgerPlayerRef,
  in: LedgerPlayerRef,
});
export type LedgerTransferCall = z.infer<typeof LedgerTransferCall>;

/** Everything captured from one produced analysis, before its gameweek is
 * played. */
export const RecordedRecommendationInput = z.object({
  userId: z.string(),
  squadId: z.string(),
  gameweek: z.number().int(),
  generatedAt: z.string(),
  objective: ObjectiveSchema,
  captain: LedgerCaptainCall,
  /** Empty when the advice was to hold. */
  transfers: z.array(LedgerTransferCall),
  chip: z.object({ recommended: ChipSchema.nullable() }),
});
export type RecordedRecommendationInput = z.infer<typeof RecordedRecommendationInput>;

export const RecordedRecommendation = RecordedRecommendationInput.extend({
  id: z.string(),
});
export type RecordedRecommendation = z.infer<typeof RecordedRecommendation>;

/** How the captaincy call actually played out. Scored against the
 * manager's own captain, not against "the best possible captain" — the
 * question this answers is "was our advice worth taking". */
export const LedgerCaptainOutcome = z.object({
  recommendedPoints: z.number(),
  ownPoints: z.number(),
  beatOwn: z.boolean(),
});
export type LedgerCaptainOutcome = z.infer<typeof LedgerCaptainOutcome>;

/** How one recommended transfer actually played out — scored regardless of
 * whether the manager took it. The counterfactual is the whole point: this
 * is "how would the in player have done against the out player", not "did
 * this transfer happen". */
export const LedgerTransferOutcome = z.object({
  out: z.string(),
  in: z.string(),
  outPoints: z.number(),
  inPoints: z.number(),
  gained: z.boolean(),
});
export type LedgerTransferOutcome = z.infer<typeof LedgerTransferOutcome>;

export const LedgerOutcome = z.object({
  captain: LedgerCaptainOutcome,
  transfers: z.array(LedgerTransferOutcome),
  scoredAt: z.string(),
});
export type LedgerOutcome = z.infer<typeof LedgerOutcome>;

export const ScoredRecommendation = RecordedRecommendation.extend({
  outcome: LedgerOutcome,
});
export type ScoredRecommendation = z.infer<typeof ScoredRecommendation>;

/** A count always travels with its rate here — a rate on its own ("100% of
 * captain calls beat the manager's own pick") reads like a track record even
 * from two gameweeks of data. Never expose `rate` without `total` next to
 * it. */
export const LedgerAggregate = z.object({
  gameweeksScored: z.number().int().min(0),
  captain: z.object({
    beatOwnCount: z.number().int().min(0),
    total: z.number().int().min(0),
    rate: z.number().min(0).max(1),
  }),
  transfers: z.object({
    gainedCount: z.number().int().min(0),
    total: z.number().int().min(0),
    rate: z.number().min(0).max(1),
  }),
});
export type LedgerAggregate = z.infer<typeof LedgerAggregate>;

/**
 * The honesty ledger aggregated across every user, for the public route —
 * never a per-user figure, never identifying. `status: 'insufficient-data'`
 * is a distinct state from a `LedgerAggregate` whose rates happen to be 0:
 * the former means "we don't have enough scored recommendations to publish
 * a rate", the latter means "the rate really is 0". Collapsing those two
 * into one shape is the zero-versus-missing trap T-085 hit three times.
 */
export const PublicLedgerAggregate = z.discriminatedUnion('status', [
  z.object({ status: z.literal('insufficient-data') }),
  z.object({ status: z.literal('ok'), aggregate: LedgerAggregate }),
]);
export type PublicLedgerAggregate = z.infer<typeof PublicLedgerAggregate>;
